import { useGetEmployeesWaiters } from '@agos/api-client/hr'
import {
  useGetOrdersOrderIdEditLogs,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import type { OrderListItem } from '@agos/api-client/model'
import {
  EmployeeAuthModal,
  FISKAL_STORNO,
  T,
  formatCents,
  formatMoney,
  lineTotalCents,
  parseMoneyToCents,
  tt,
  useAuth,
  lineVatLabel,
  ordersLinesShareOneRate,
  sharedVatLabel,
} from '@agos/shared'
import { cn } from '@agos/ui'
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit2,
  Globe,
  History,
  MapPin,
  Phone,
  RotateCcw,
  Star,
  Store,
  ShoppingBag,
  Truck,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  ORDER_STATUS_LABELS,
  ORDER_TYPE_LABELS,
  PAYMENT_METHOD_LABELS,
  orderStatusLabel,
  paymentMethodLabel,
} from '../order-status'
import {
  describeEditLogChange,
  editLogFieldLabel,
  editedFlagLabel,
  type EditLogEntry,
} from './edit-log'
import { discountSource } from './discount-source'
import { needsWaiterBeforeClosing, waiterOptions } from '../order-builder/waiter-choice'
import {
  ALL_STATUSES,
  PROTECTED_STATUSES,
  availableTransitions,
  canReturnToKitchen,
} from './status-transitions'

/**
 * One row of the archive, and everything that opens underneath it.
 *
 * The row is the whole screen's reason to exist: it is where a delivered order
 * gets corrected. Every control below the fold either moves money or moves the
 * record of who moved it, so each is behind its own permission and most are
 * behind a step-up prompt.
 */

const TYPE_ICONS: Record<string, typeof Store> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

/**
 * The pill palette. Kept as its own map rather than reused from
 * `orderStatusColor` because the archive draws bordered pills and `StatusBadge`
 * draws filled ones — same vocabulary, different shape.
 */
const STATUS_PILL_CLASSES: Record<string, string> = {
  new: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  preparing: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  ready: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  on_the_way: 'bg-violet-500/10 text-violet-500 border-violet-500/20',
  at_address: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  delivered: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  quality_check: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
}

const TRANSITION_BUTTON_CLASSES: Record<string, string> = {
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20',
  quality_check: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20 hover:bg-cyan-500/20',
  delivered: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20',
}

const TRANSITION_ICONS: Record<string, typeof Ban> = {
  cancelled: Ban,
  quality_check: Star,
  delivered: CheckCircle2,
}

const SELECT_CLASSES =
  'flex-1 bg-background border border-amber-500/30 rounded-xl px-3 py-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/30'

const ADMIN_BUTTON_CLASSES =
  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30 hover:bg-amber-500/30 transition-all whitespace-nowrap'

/** The lock the row prints next to anything that will ask for a password. */
function StepUpHint() {
  return (
    <span className="text-[9px] font-medium opacity-60 flex items-center gap-0.5">
      🔒 <T>overenie</T>
    </span>
  )
}

function formatDateTime(value: string | undefined): string {
  return value ? new Date(value).toLocaleString('sk-SK') : '-'
}

export interface OrderRowHandlers {
  onStatusRequest: (order: OrderListItem, newStatus: string, forceAuth?: boolean) => void
  onPaymentMethodRequest: (order: OrderListItem, newMethod: string) => void
  onReturnToKitchen: (order: OrderListItem) => void
  /** The register answered; the page decides what the manager is told. */
  onStornoSuccess: (response: Record<string, unknown> | undefined) => void
  onRefresh: () => void
}

export function OrderRow({
  order,
  onStatusRequest,
  onPaymentMethodRequest,
  onReturnToKitchen,
  onStornoSuccess,
  onRefresh,
}: { order: OrderListItem } & OrderRowHandlers) {
  const [expanded, setExpanded] = useState(false)
  const [stornoTarget, setStornoTarget] = useState<OrderListItem | null>(null)
  const [forcedStatus, setForcedStatus] = useState('')
  const [forcedPaymentMethod, setForcedPaymentMethod] = useState('')
  const { hasPermission, user } = useAuth()

  // §9.5: the per-line tax is drawn only where an order mixes rates. Computed once for the
  // row rather than per line, so the decision is the order's and not each line's.
  const oneRate = ordersLinesShareOneRate(order.items ?? [])
  const orderVat = sharedVatLabel(order.items ?? [])

  const isMainAdmin = user?.systemRole === 'main_admin'
  const orderId = order.id ?? 0

  // Only fetched once the row is open. An archive page holds fifty rows and
  // almost none of them are ever expanded.
  const { data: editLogs } = useGetOrdersOrderIdEditLogs(orderId, {
    query: { enabled: expanded && orderId > 0, staleTime: 60_000 },
  })

  /**
   * §9.333 - whether this order cannot be closed until somebody owns it.
   *
   * The channels are read rather than guessed: `paymentNeedsWaiter` used to hardcode
   * `restaurant || bar`, which missed `na_sekeru` - the owner's own channel and the one that was
   * stuck. Cached hard: a franchise's channel list does not change while an archive page is open.
   */
  const { data: channelData } = useGetSalesChannels({ query: { staleTime: 30 * 60_000 } })
  const channels = useMemo(
    () => (Array.isArray(channelData) ? channelData : []),
    [channelData],
  )
  const needsWaiter = needsWaiterBeforeClosing(order, channels)
  const [waiterId, setWaiterId] = useState<number | null>(null)
  const { data: waiterData, isSuccess: waitersLoaded } = useGetEmployeesWaiters({
    // Only asked for when the row is actually offering the picker.
    query: { enabled: expanded && needsWaiter },
  })
  const waiters = useMemo(() => waiterOptions(waiterData), [waiterData])
  const assignWaiter = usePatchOrdersOrderId()

  const transitions = availableTransitions(order.status, {
    canChangeStatus: hasPermission('change_order_status'),
    canCancel: hasPermission('cancel_order'),
  })
  const showReturnToKitchen = canReturnToKitchen({
    systemRole: user?.systemRole,
    hasPermission,
    status: order.status,
  })
  // Legacy spells this `… || isMainAdmin`. It does not need to: `hasPermission`
  // already answers true for a main_admin without consulting the matrix.
  const canStorno = hasPermission('fiskal') || hasPermission('fiskal_storno')
  const showStornoButton = canStorno && !!order.fiskalPrintedAt && !order.fiskalStornoAt

  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Store
  const itemsSummary =
    order.items?.map((line) => `${line.quantity ?? 0}× ${line.productName ?? ''}`).join(', ') || '-'

  return (
    <>
      <tr
        className={cn(
          'border-b border-border hover:bg-muted/20 transition-colors cursor-pointer select-none',
          expanded && 'bg-muted/10',
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            )}
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold text-primary">
                  #{order.orderNumber}
                </span>
                <SourceBadge source={order.source} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString('sk-SK', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '-'}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3.5">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
            <TypeIcon className="w-3.5 h-3.5" />
            {ORDER_TYPE_LABELS[order.type ?? ''] ?? order.type}
          </span>
        </td>
        <td className="px-4 py-3.5">
          {order.customerName ? (
            <span className="text-sm font-semibold text-foreground">{order.customerName}</span>
          ) : (
            <span className="text-xs text-muted-foreground italic">-</span>
          )}
          {order.customerPhone && (
            <div className="text-[10px] text-muted-foreground">{order.customerPhone}</div>
          )}
          {/* §9.138 - the archive is read by scanning it for *which order went to Hlavná 12*, and an
            * address that costs a click per row cannot be scanned. Truncated because one long
            * address must not make the whole table ragged; the expanded row keeps it whole. */}
          {order.customerAddress && (
            <div
              data-row-address
              title={order.customerAddress}
              className="flex items-center gap-1 text-[10px] text-muted-foreground truncate max-w-[190px]"
            >
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {order.customerAddress}
            </div>
          )}
        </td>
        <td className="px-4 py-3.5">
          <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">
            {itemsSummary}
          </div>
        </td>
        <td className="px-4 py-3.5 text-right">
          <span className="font-bold text-foreground text-sm">{formatMoney(order.total)}</span>
          <p className="text-[10px] text-muted-foreground">
            {paymentMethodLabel(order.paymentMethod)}
          </p>
          {/* §9.210 - "on every order", and the row somebody scans is where *every* lives. The
            * expanded bill carries the amount and the operator; this is the fact of it. */}
          {(order.discount ?? 0) > 0 && (
            <p
              className="text-[10px] font-medium text-emerald-600"
              data-discount-badge={discountSource(order).kind}
            >
              {discountSource(order).label}
            </p>
          )}
        </td>
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            <StatusPill status={order.status} />
            <EditedFlag order={order} />
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-border bg-muted/5">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  <T>Detaily objednávky</T>
                </p>
                {order.customerAddress && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {order.customerAddress}
                  </div>
                )}
                {order.notes && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-600">
                    📝 {order.notes}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground space-y-0.5">
                  <p>
                    <T>Vytvorená:</T> {formatDateTime(order.createdAt)}
                  </p>
                  {order.updatedAt !== order.createdAt && (
                    <p>
                      <T>Aktualizovaná:</T> {formatDateTime(order.updatedAt)}
                    </p>
                  )}
                  {order.employeeName && (
                    <p>
                      <T>Čašník:</T> {order.employeeName}
                    </p>
                  )}
                  {order.driverName && (
                    <p>
                      <T>Šofér:</T> {order.driverName}
                    </p>
                  )}
                  {order.isEdited && order.updatedByName && (
                    <p className="text-red-500 font-medium">
                      <T>Upravil:</T> {order.updatedByName}
                    </p>
                  )}
                  {order.updatedByName && !order.isEdited && (
                    <p
                      className={
                        order.status === 'cancelled'
                          ? 'text-red-500 font-medium'
                          : 'text-sky-500 font-medium'
                      }
                    >
                      {order.status === 'cancelled' ? tt('Zrušil') : tt('Zmenil stav')}:{' '}
                      {order.updatedByName}
                    </p>
                  )}
                  {order.confirmedByName && (
                    <p className="text-violet-500 font-medium">
                      <T>Overil:</T> {order.confirmedByName}
                    </p>
                  )}
                  {order.changeReason && (
                    <p className="text-muted-foreground">
                      <T>Dôvod:</T> {order.changeReason}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  <T>Položky</T>
                </p>
                <div className="space-y-1">
                  {order.items?.map((line) => (
                    <div
                      key={line.id}
                      className="flex justify-between text-xs bg-muted/50 rounded-lg px-3 py-1.5"
                    >
                      <span className="font-medium text-foreground">
                        <span className="text-primary font-bold">{line.quantity}×</span>{' '}
                        <T>{line.productName ?? ''}</T>
                      </span>
                      <span className="flex items-center gap-2">
                        {/* §9.5's DPH bullet. Drawn only where the order mixes rates - food at 5 %
                            beside a drink at 19 % - because repeating one rate down every line of a
                            single-rate order is noise, and single-rate is still almost all of them.
                            The line's own snapshot, never the product's: the two disagree on 77 % of
                            stored lines, so reading the product would restate three orders in four. */}
                        {!oneRate && lineVatLabel(line) !== '' && (
                          <span className="text-[10px] text-muted-foreground" data-line-vat>
                            {lineVatLabel(line)}
                          </span>
                        )}
                        <span className="font-bold text-foreground">
                          {formatCents(lineTotalCents(line.unitPrice, line.quantity ?? 0))}
                        </span>
                      </span>
                    </div>
                  ))}
                  {/* §9.210 - which discount, not only that there was one. */}
                  {(order.discount ?? 0) > 0 &&
                    (() => {
                      const source = discountSource(order)
                      return (
                        <div
                          className="flex justify-between text-xs px-3 py-1 text-emerald-600"
                          data-discount-kind={source.kind}
                        >
                          <span>
                            <T>Zľava</T>
                            {source.label !== '' && (
                              <span
                                className={cn(
                                  'ml-1',
                                  source.kind === 'unrecorded'
                                    ? 'text-muted-foreground italic'
                                    : 'text-foreground font-medium',
                                )}
                                data-discount-source
                              >
                                · {source.label}
                              </span>
                            )}
                            {source.operator !== null && (
                              <span className="ml-1 text-muted-foreground" data-discount-operator>
                                ({source.operator})
                              </span>
                            )}
                          </span>
                          <span>−{formatMoney(order.discount)}</span>
                        </div>
                      )
                    })()}
                  {(order.deliveryCost ?? 0) > 0 && (
                    <div className="flex justify-between text-xs px-3 py-1 text-muted-foreground">
                      <span>
                        <T>Dovoz</T>
                      </span>
                      <span>+{formatMoney(order.deliveryCost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold px-3 pt-1 border-t border-border">
                    <span>
                      <T>Spolu</T>
                    </span>
                    <span className="text-primary">{formatMoney(order.total)}</span>
                  </div>
                  {/* Stated once when every line shares a rate, which on the restored data is every
                      order: A is dine-in at 5 %, B is delivery and drinks at 19 %, so the rate
                      follows the channel and a bill does not mix them. Drawing the tax only on a
                      mixed-rate order would have drawn it on none of the 10 785, which is not what
                      §9.5 asks for. */}
                  {orderVat !== '' && (
                    <div className="flex justify-between text-[11px] px-3 text-muted-foreground">
                      <span>
                        <T>DPH</T>
                      </span>
                      <span data-order-vat>{orderVat}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* §9.333 - the owner: *"its in zoznam & archív as kontrola kvality, but we cant move
                  it."* The transition button below is offered and the server refuses it, because
                  `guardClosable` will not close an order on a table channel without a waiter - and
                  `employeeId` was settable nowhere this row can reach. Shown only when all three of
                  the server's own conditions hold, so a row where the question does not arise shows
                  nothing. */}
              {needsWaiter && (
                <div className="space-y-2" data-needs-waiter={orderId}>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Čašník</T>
                  </p>
                  <p className="text-[11px] text-amber-500">
                    <T>
                      Bez priradeného čašníka sa objednávka nedá uzatvoriť - niekto musí mať tržbu
                      na sebe.
                    </T>
                  </p>
                  <div className="flex gap-2">
                    <select
                      data-assign-waiter={orderId}
                      aria-label={tt('Priradiť čašníka')}
                      value={waiterId ?? ''}
                      onChange={(event) =>
                        setWaiterId(event.target.value === '' ? null : Number(event.target.value))
                      }
                      className="flex-1 min-h-[36px] rounded-xl border border-border bg-background px-3 text-xs"
                    >
                      <option value="">{tt('- vyberte čašníka -')}</option>
                      {waiters.map((waiter) => (
                        <option key={waiter.id} value={waiter.id}>
                          {waiter.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-assign-waiter-save={orderId}
                      disabled={waiterId === null || assignWaiter.isPending}
                      onClick={(event) => {
                        event.stopPropagation()
                        void assignWaiter
                          .mutateAsync({ orderId, data: { employeeId: waiterId } })
                          .then(() => onRefresh())
                      }}
                      className="min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
                    >
                      <T>Priradiť</T>
                    </button>
                  </div>
                  {waitersLoaded && waiters.length === 0 && (
                    <p className="text-[11px] text-muted-foreground" data-no-waiters>
                      <T>Žiadni čašníci s dochádzkou - pridajte dochádzku.</T>
                    </p>
                  )}
                </div>
              )}

              {transitions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Zmena stavu</T>
                  </p>
                  <div className="space-y-2">
                    {transitions.map((next) => {
                      const Icon = TRANSITION_ICONS[next]
                      return (
                        <button
                          key={next}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            onStatusRequest(order, next)
                          }}
                          className={cn(
                            'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all',
                            TRANSITION_BUTTON_CLASSES[next] ??
                              'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20',
                          )}
                        >
                          <span className="flex items-center gap-1.5">
                            {Icon && <Icon className="w-3.5 h-3.5" />}
                            {orderStatusLabel(next)}
                          </span>
                          {PROTECTED_STATUSES.includes(next) && <StepUpHint />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {transitions.length === 0 && !isMainAdmin && !showReturnToKitchen && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Stav</T>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>Táto objednávka je ukončená.</T>
                  </p>
                </div>
              )}

              {showReturnToKitchen && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Vrátenie do kuchyne</T>
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      onReturnToKitchen(order)
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border bg-amber-500/10 text-amber-500 border-amber-500/20 hover:bg-amber-500/20 transition-all"
                  >
                    <span className="flex items-center gap-1.5">
                      <RotateCcw className="w-3.5 h-3.5" /> <T>Vrátiť do kuchyne</T>
                    </span>
                    <StepUpHint />
                  </button>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    <T>
                      Objednávka sa znovu objaví v kuchyni a odráta sa šoférovi, ktorý ju doručil. Po
                      opätovnom doručení sa pripíše šoférovi, ktorý ju doručí.
                    </T>
                  </p>
                </div>
              )}

              {showStornoButton && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Fiškálny doklad</T>
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      setStornoTarget(order)
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20 transition-all"
                  >
                    <span className="flex items-center gap-1.5">
                      <Ban className="w-3.5 h-3.5" /> <T>Storno fiškálneho dokladu</T>
                    </span>
                    <StepUpHint />
                  </button>
                </div>
              )}

              {order.fiskalStornoAt && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Fiškálny doklad</T>
                  </p>
                  <div className="px-3 py-2 rounded-xl border border-red-500/20 bg-red-500/5 text-xs text-red-400">
                    <div className="flex items-center gap-1.5 font-bold">
                      <Ban className="w-3.5 h-3.5" /> <T>Stornovaný</T>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {formatDateTime(order.fiskalStornoAt)}
                      {order.fiskalStornoBy ? ` · ${order.fiskalStornoBy}` : ''}
                    </div>
                  </div>
                </div>
              )}

              {isMainAdmin && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" /> <T>Zmeniť stav (admin)</T>
                  </p>
                  <div className="flex gap-2">
                    <select
                      aria-label={tt('Zmeniť stav (admin)')}
                      value={forcedStatus}
                      onChange={(event) => setForcedStatus(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      className={SELECT_CLASSES}
                    >
                      <option value="" disabled>
                        {tt('- vybrať stav -')}
                      </option>
                      {ALL_STATUSES.filter((status) => status !== order.status).map((status) => (
                        <option key={status} value={status}>
                          {orderStatusLabel(status)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        if (forcedStatus) onStatusRequest(order, forcedStatus, true)
                      }}
                      className={ADMIN_BUTTON_CLASSES}
                    >
                      <Star className="w-3 h-3" /> <T>Potvrdiť</T>
                    </button>
                  </div>
                </div>
              )}

              {isMainAdmin && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" /> <T>Zmeniť spôsob platby (admin)</T>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <T>Aktuálne:</T>{' '}
                    <span className="font-semibold text-foreground">
                      {paymentMethodLabel(order.paymentMethod)}
                    </span>
                  </p>
                  <div className="flex gap-2">
                    <select
                      aria-label={tt('Zmeniť spôsob platby (admin)')}
                      value={forcedPaymentMethod}
                      onChange={(event) => setForcedPaymentMethod(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      className={SELECT_CLASSES}
                    >
                      <option value="" disabled>
                        {tt('- vybrať spôsob platby -')}
                      </option>
                      {Object.entries(PAYMENT_METHOD_LABELS)
                        .filter(([method]) => method !== order.paymentMethod)
                        .map(([method, label]) => (
                          <option key={method} value={method}>
                            {label}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        if (forcedPaymentMethod) onPaymentMethodRequest(order, forcedPaymentMethod)
                      }}
                      className={ADMIN_BUTTON_CLASSES}
                    >
                      <Star className="w-3 h-3" /> <T>Potvrdiť</T>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <EditHistory entries={(editLogs ?? []) as EditLogEntry[]} />
          </td>
        </tr>
      )}

      {stornoTarget && (
        <EmployeeAuthModal
          meta={{
            orderId: stornoTarget.id ?? 0,
            orderNumber: stornoTarget.orderNumber ?? '',
            orderTotalCents: parseMoneyToCents(stornoTarget.total),
            newStatus: FISKAL_STORNO,
          }}
          onClose={() => setStornoTarget(null)}
          onSuccess={(_confirmedBy, response) => {
            setStornoTarget(null)
            // `AuthSuccessPayload` is `object` since the order responses became records; this
            // handler wants the storno body, which is read by name further down.
            onStornoSuccess(response as Record<string, unknown> | undefined)
            onRefresh()
          }}
        />
      )}
    </>
  )
}

export function SourceBadge({ source }: { source: string | undefined }) {
  if (source === 'web') {
    return (
      <span className="flex items-center gap-0.5 text-[9px] font-bold rounded px-1 py-0.5 leading-none bg-blue-500/20 text-blue-400 border border-blue-500/35">
        <Globe className="w-2 h-2" />
        Web
      </span>
    )
  }
  if (source === 'phone') {
    return (
      <span className="flex items-center gap-0.5 text-[9px] font-bold rounded px-1 py-0.5 leading-none bg-orange-500/20 text-orange-400 border border-orange-500/35">
        <Phone className="w-2 h-2" />
        <T>Telefón</T>
      </span>
    )
  }
  return null
}

export function StatusPill({ status }: { status: string | undefined }) {
  return (
    <span
      className={cn(
        'text-xs font-bold px-2.5 py-1 rounded-full border',
        STATUS_PILL_CLASSES[status ?? ''] ?? 'bg-muted text-muted-foreground',
      )}
    >
      {ORDER_STATUS_LABELS[status ?? ''] ?? status}
    </span>
  )
}

/**
 * §9.286, FB-443 — the badge that says an order was edited.
 *
 * **The glyph and the classes are legacy's and stay legacy's.** What is added is an accessible name:
 * the explanation was a `title` alone, and `title` is a hover tooltip — there is no hover on a
 * tablet, which is the device O1 is about, and none for a screen reader on any device. So on the
 * owner's own screen this badge said nothing at all.
 *
 * Deliberately **not** widened. `/lists` at a tablet width was one of O1's overflow findings
 * (§9.270); putting a word inside this pill is the one change that would re-open it.
 */
export function EditedFlag({ order }: { order: OrderListItem }) {
  if (!order.isEdited) return null
  const label = editedFlagLabel(order)
  return (
    <span
      className="text-xs font-black text-red-500 bg-red-500/15 border border-red-500/30 rounded-full px-1.5 py-0.5 leading-none"
      title={label}
      aria-label={label}
      data-edited-flag
    >
      !
    </span>
  )
}

function EditHistory({ entries }: { entries: EditLogEntry[] }) {
  if (entries.length === 0) return null

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> <T>História zmien</T> ({entries.length})
      </p>
      <div className="space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="bg-red-500/5 border border-red-500/10 rounded-xl px-3 py-2 text-xs"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-red-500 flex items-center gap-1">
                <Edit2 className="w-3 h-3" />
                {entry.employeeName ?? tt('Neznámy')}{' '}
                <span className="font-normal text-muted-foreground">({entry.employeeRole})</span>
              </span>
              <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
            </div>
            <div className="space-y-0.5 text-muted-foreground">
              {Object.entries(entry.changes ?? {}).map(([field, change]) => {
                const described = describeEditLogChange(field, change)
                if (described.kind === 'lines') {
                  return (
                    <div key={field}>
                      <span className="font-semibold text-foreground">
                        <T>Položky zmenené:</T>
                      </span>
                      <div className="ml-2 mt-0.5">
                        <div className="text-red-400 line-through">{described.before}</div>
                        <div className="text-emerald-500">{described.after}</div>
                      </div>
                    </div>
                  )
                }
                if (described.kind === 'added') {
                  return (
                    <div key={field}>
                      <span className="font-semibold text-foreground">
                        <T>Pridané položky:</T>
                      </span>
                      <div className="ml-2 mt-0.5 text-emerald-500">{described.added}</div>
                    </div>
                  )
                }
                return (
                  <div key={field}>
                    <span className="font-semibold text-foreground">
                      {editLogFieldLabel(field)}:
                    </span>{' '}
                    <span className="line-through text-red-400">{described.before}</span>
                    {' → '}
                    <span className="text-emerald-500">{described.after}</span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
